import { GripVertical } from "lucide-react";
import { Group, Panel, Separator } from "react-resizable-panels";

import { cn } from "@/lib/utils/shadcn";

// `react-resizable-panels` v4 renamed the axis prop to `orientation` (and
// dropped the `data-panel-group-direction` attribute this component used to
// key its vertical-only classes off) — every call site in this app still
// passes `direction`, so that's kept as the public prop here and translated,
// rather than touching every `<ResizablePanelGroup direction="...">` in the
// app. Every existing `direction="horizontal"` usage "worked" only because
// horizontal is the library's own default when orientation is never
// forwarded — `direction="vertical"` was silently a no-op until this fix.
const ResizablePanelGroup = ({
  className,
  direction = "horizontal",
  ...props
}) => (
  <Group
    orientation={direction}
    className={cn(
      "flex h-full w-full",
      direction === "vertical" && "flex-col",
      className,
    )}
    {...props}
  />
);

const ResizablePanel = Panel;

// The separator has no `direction` prop of its own — orientation is inferred
// from its enclosing Group at drag-mechanics level regardless, but for the
// *visual* cross-axis styling below, `aria-orientation` is the one live
// signal the library actually renders. Note it's the separator's own line
// orientation, the inverse of the group's: a "vertical" (stacked) group's
// separator is a horizontal line, so `aria-orientation="horizontal"` is the
// vertical-group case here (mirrors the old, non-functional
// `data-[panel-group-direction=vertical]` selectors 1:1).
const ResizableHandle = ({ withHandle, className, ...props }) => (
  <Separator
    className={cn(
      "relative flex w-px items-center justify-center bg-border after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:outline-none aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:-translate-y-1/2 aria-[orientation=horizontal]:after:translate-x-0 [&[aria-orientation=horizontal]>div]:rotate-90",
      className,
    )}
    {...props}>
    {withHandle && (
      <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border bg-border">
        <GripVertical className="h-2.5 w-2.5" />
      </div>
    )}
  </Separator>
);

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
